//-- Copyright (c) 2021 TMT Insights Group, LLC
import React, { ReactNode, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { darken, lighten } from '@mui/material';

interface SlideToggleProps {
  left: {
    label: string | ReactNode;
    value: string | boolean;
  };
  right: {
    label: string | ReactNode;
    value: string | boolean;
  };
  callback: (value: string | boolean) => void;
  alternateColors?: boolean;
  defaultUnchecked?: boolean;
  disabled?: boolean;
  className?: string;
}

const SlideToggle = ({ left, right, callback, alternateColors = false, defaultUnchecked = false, disabled = false, className }: SlideToggleProps) => {
  // defaults to right option chosen
  const [checked, setChecked] = useState<boolean>(!defaultUnchecked);

  const onToggle = () => {
    const updatedChoice = !checked;
    callback(updatedChoice ? right.value : left.value);
    setChecked(updatedChoice);
  };

  // use to handle updating of toggled state when defaultUnchecked props has been updated by the parent
  useEffect(() => {
    setChecked(!defaultUnchecked);
  }, [defaultUnchecked]);

  return (
    <>
      <ToggleWrapper
        onClick={() => {
          if (!disabled) onToggle();
        }}
        alternateColors={alternateColors}
        disabled={disabled}
        className={className}
      >
        <ToggleCheckbox type="checkbox" checked={checked} readOnly />
        <Slider className="slider" alternateColors={alternateColors} disabled={disabled} />
        <OptionLabel
          className="label"
          checked={checked}
          alternateColors={alternateColors}
          iconConfig={typeof left.label !== 'string'}
          disabled={disabled}
          secondary
        >
          {left.label}
        </OptionLabel>
        <OptionLabel
          className="label"
          checked={checked}
          alternateColors={alternateColors}
          iconConfig={typeof right.label !== 'string'}
          disabled={disabled}
          primary
        >
          {right.label}
        </OptionLabel>
      </ToggleWrapper>
    </>
  );
};

export default SlideToggle;

type ElementWrapperProps = {
  alternateColors?: boolean;
  disabled?: boolean;
  checked?: boolean;
  primary?: boolean;
  secondary?: boolean;
  iconConfig?: boolean;
};

const ToggleWrapper = styled.div<ElementWrapperProps>`
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  height: 36px;
  background: ${({ theme, alternateColors, disabled }) => {
    const colorType = alternateColors ? 'alternate' : 'base';
    const status = !disabled ? 'enabled' : 'disabled';
    const settings = {
      base: {
        light: {
          enabled: theme.palette.grey['300'],
          disabled: theme.palette.grey['300']
        },
        dark: {
          enabled: theme.palette.background.default,
          disabled: theme.palette.background.default
        }
      },
      alternate: {
        light: {
          enabled: theme.palette.grey['300'],
          disabled: theme.palette.grey['300']
        },
        dark: {
          enabled: lighten(theme.palette.background.paper, 0.01),
          disabled: darken(theme.palette.background.paper, 0.1)
        }
      }
    };

    return settings[colorType][theme.palette.mode][status];
  }};
  border: 1px solid ${({ theme }) => theme.palette.action.hover};
  border-radius: 37px;
  z-index: 1;

  & input[type='checkbox']:checked + .slider {
    transform: translateX(calc(100% - 5px));
  }
`;

const OptionLabel = styled.div<ElementWrapperProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  transition: color 0.3s ease;
  font-weight: bold;
  padding: ${({ primary }) => (primary ? '0px 14px 0px 10px' : '0px 10px 0px 14px')};
  font-size: ${({ iconConfig }) => (iconConfig ? '1.5rem' : '1rem')};
  color: ${({ theme, checked, alternateColors, primary, disabled }) => {
    const colorType = alternateColors ? 'alternate' : 'base';
    const checkValue = checked ? 'checked' : 'unchecked';
    const selected = primary ? 'primary' : 'secondary';
    const status = !disabled ? 'enabled' : 'disabled';
    const settings = {
      base: {
        light: {
          enabled: {
            checked: {
              primary: theme.palette.primary.main, // grid toggle - light mode - ON icon color
              secondary: theme.palette.grey['400'] // grid toggle - light mode - OFF icon color
            },
            unchecked: {
              primary: theme.palette.grey['400'],
              secondary: theme.palette.primary.main
            }
          },
          disabled: {
            checked: {
              primary: theme.palette.grey['500'],
              secondary: theme.palette.grey['500']
            },
            unchecked: {
              primary: theme.palette.grey['500'],
              secondary: theme.palette.grey['500']
            }
          }
        },
        dark: {
          enabled: {
            checked: {
              primary: theme.palette.primary.main, // grid toggle - dark mode - ON/RIGHT icon color
              secondary: theme.palette.grey['700']
            },
            unchecked: {
              primary: theme.palette.grey['700'], // grid toggle - dark mode - OFF/RIGHT icon color
              secondary: theme.palette.primary.main
            }
          },
          disabled: {
            checked: {
              primary: theme.palette.grey['500'],
              secondary: theme.palette.grey['500']
            },
            unchecked: {
              primary: theme.palette.grey['500'],
              secondary: theme.palette.grey['500']
            }
          }
        }
      },
      alternate: {
        light: {
          enabled: {
            checked: {
              primary: 'white', // light mode - checked text color
              secondary: lighten(theme.palette.text.primary, 0.5)
            },
            unchecked: {
              primary: lighten(theme.palette.text.primary, 0.5), // light mode - unchecked text color
              secondary: 'white' // light mode - checked text color
            }
          },
          disabled: {
            checked: {
              primary: theme.palette.grey['300'],
              secondary: theme.palette.grey['500']
            },
            unchecked: {
              primary: theme.palette.grey['500'],
              secondary: theme.palette.grey['300']
            }
          }
        },
        dark: {
          enabled: {
            checked: {
              primary: 'white', // dark mode - when toggled right, text color
              secondary: theme.palette.grey['500'] // dark mode - when toggled right, text color for the off/left side
            },
            unchecked: {
              primary: theme.palette.grey['500'], // dark mode - when toggled left, text color for the right side
              secondary: 'white'
            }
          },
          disabled: {
            checked: {
              primary: 'white',
              secondary: theme.palette.grey['500']
            },
            unchecked: {
              primary: theme.palette.grey['500'],
              secondary: 'rgba(255, 255, 255, 0.5)'
            }
          }
        }
      }
    };

    return settings[colorType][theme.palette.mode][status][checkValue][selected];
  }};
`;

const Slider = styled.span<ElementWrapperProps>`
  position: absolute;
  width: 50%;
  height: 30px;
  z-index: 0;
  top: 2px;
  left: 2.5px;
  bottom: 0;
  right: 0;
  border-radius: 29px;
  transition: transform 0.3s ease;
  background-color: ${({ theme, alternateColors, disabled }) => {
    const colorType = alternateColors ? 'alternate' : 'base';
    const status = !disabled ? 'enabled' : 'disabled';
    const settings = {
      base: {
        light: {
          enabled: 'white',
          disabled: theme.palette.grey['100']
        },
        dark: {
          enabled: `${theme.palette.primary.main}2a`,
          disabled: `${theme.palette.primary.main}2a`
        }
      },
      alternate: {
        light: {
          enabled: theme.palette.primary.main, // files toggle - light mode enabled bg
          disabled: theme.palette.grey['500']
        },
        dark: {
          enabled: theme.palette.primary.main, // files toggle - dark mode enabled bg
          disabled: darken(theme.palette.primary.main, 0.5)
        }
      }
    };

    return settings[colorType][theme.palette.mode][status];
  }};
`;

const ToggleCheckbox = styled.input`
  display: none;
`;
